import { useState, useCallback } from 'react';
import type { FieldErrors } from './form-validation.types';

/**
 * Manages a per-field error map.
 *
 * @returns
 *  - `fieldErrors` – current error map
 *  - `setFieldErrors` – replace the whole map (call after collect-all-pass)
 *  - `clearFieldError` – clear a single field's error as the user edits it
 *  - `resetFieldErrors` – clear the entire map (e.g. on modal open)
 */
export function useFieldErrors<T extends string>() {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<T>>({});

  const clearFieldError = useCallback((field: T) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const resetFieldErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  return { fieldErrors, setFieldErrors, clearFieldError, resetFieldErrors };
}

/**
 * Scrolls the first invalid field into view using the convention
 * `id="${prefix}-${fieldName}"`.
 */
export function scrollToFirstError<T extends string>(prefix: string, errors: FieldErrors<T>): void {
  const firstKey = Object.keys(errors)[0] as T | undefined;
  if (firstKey) {
    document.getElementById(`${prefix}-${firstKey}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
}
